import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, User, Building2, ArrowLeft, Gift } from 'lucide-react';
import { ReferralCodeInput } from '../components/ReferralSection';
import { LogoMarkB } from '../components/Logo';

const RegisterPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { register } = useAuth();

    const defaultType = searchParams.get('type') || 'seller';
    const defaultReferralCode = searchParams.get('ref') || '';
    
    const [userType, setUserType] = useState(defaultType);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [referralCode, setReferralCode] = useState(defaultReferralCode);
    const [referralValid, setReferralValid] = useState(false);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        // Agent fields
        agency_name: '',
        city: '',
        commission_rate: '',
        bio: '',
    });

    // If coming with a referral code, default to agent registration
    useEffect(() => {
        if (defaultReferralCode) {
            setUserType('agent');
        }
    }, [defaultReferralCode]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const userData = {
                ...formData,
                user_type: userType,
                commission_rate: formData.commission_rate ? parseFloat(formData.commission_rate) : null,
                specializations: [],
                referral_code: userType === 'agent' && referralCode ? referralCode : null,
            };

            await register(userData);
            toast.success('Compte créé avec succès !');
            navigate(userType === 'agent' ? '/dashboard' : '/agents');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="register-page">
            <div className="w-full max-w-lg">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-muted-foreground hover:text-scalimmo-navy mb-8 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Retour à l'accueil
                </Link>

                <Card className="shadow-soft">
                    <CardHeader className="text-center pb-2">
                        <div className="flex justify-center mb-4">
                            <LogoMarkB size={56} />
                        </div>
                        <CardTitle className="font-heading text-2xl">Créer un compte</CardTitle>
                        <CardDescription>
                            Rejoignez Scalimmo et trouvez le meilleur agent
                        </CardDescription>
                    </CardHeader>

                    <CardContent>
                        <Tabs value={userType} onValueChange={setUserType} className="mb-6">
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger
                                    value="seller"
                                    className="flex items-center gap-2"
                                    data-testid="tab-seller"
                                >
                                    <User className="w-4 h-4" />
                                    Vendeur
                                </TabsTrigger>
                                <TabsTrigger
                                    value="agent"
                                    className="flex items-center gap-2"
                                    data-testid="tab-agent"
                                >
                                    <Building2 className="w-4 h-4" />
                                    Agent
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="first_name">Prénom</Label>
                                    <Input
                                        id="first_name"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        required
                                        data-testid="input-first-name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="last_name">Nom</Label>
                                    <Input
                                        id="last_name"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        required
                                        data-testid="input-last-name"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    data-testid="input-email"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="phone">Téléphone</Label>
                                <Input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    data-testid="input-phone"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Mot de passe</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        minLength={6}
                                        data-testid="input-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {/* Agent-specific fields */}
                            {userType === 'agent' && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="agency_name">Nom de l'agence</Label>
                                        <Input
                                            id="agency_name"
                                            name="agency_name"
                                            value={formData.agency_name}
                                            onChange={handleChange}
                                            data-testid="input-agency"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="city">Ville</Label>
                                            <Input
                                                id="city"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                data-testid="input-city"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="commission_rate">Honoraires (%)</Label>
                                            <Input
                                                id="commission_rate"
                                                name="commission_rate"
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="10"
                                                value={formData.commission_rate}
                                                onChange={handleChange}
                                                data-testid="input-commission"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="bio">Présentation</Label>
                                        <textarea
                                            id="bio"
                                            name="bio"
                                            value={formData.bio}
                                            onChange={handleChange}
                                            rows={3}
                                            className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                            data-testid="input-bio"
                                        />
                                    </div>

                                    {/* Referral Code */}
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <Gift className="w-4 h-4 text-scalimmo-gold" />
                                            Code de parrainage
                                        </Label>
                                        <ReferralCodeInput
                                            value={referralCode}
                                            onChange={setReferralCode}
                                            onValidate={setReferralValid}
                                        />
                                    </div>
                                </>
                            )}

                            <Button
                                type="submit"
                                className="w-full btn-primary"
                                disabled={loading}
                                data-testid="submit-register"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="spinner" />
                                        Création...
                                    </span>
                                ) : (
                                    "Créer mon compte"
                                )}
                            </Button>
                        </form>

                        <p className="text-center text-sm text-muted-foreground mt-6">
                            Déjà inscrit ?{' '}
                            <Link to="/login" className="text-scalimmo-navy font-medium hover:underline">
                                Se connecter
                            </Link>
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default RegisterPage;
